import React from 'react';
import { Box, Image, Text, Flex, Tag } from '@chakra-ui/react';
import { MdLocalOffer } from 'react-icons/md';
import MarkdownRenderer from "../../Common/MarkdownRenderer";

function RelatedPostCard({ image, tag, title, desc, post_desc, onClick, isVideo, videoUrl }) {
  // Safely handle tag field - ensure it's always a string
  const safeTag = typeof tag === 'object' ? 
    (tag?.fullName || tag?.name || tag?.text || "News") : 
    (tag || "News");

  // Helper function to check if post has valid media
  const hasValidMedia = () => {
    const hasValidImage = image && 
      !image.includes('/clients/') && 
      !image.includes('/png/placeholder.png') &&
      !image.includes('placeholder');
    const hasValidVideo = isVideo && videoUrl;
    return hasValidImage || hasValidVideo;
  };

  // Helper component for rendering markdown text
  const MarkdownText = ({ children, fontSize, color, noOfLines, ...props }) => {
    const hasMarkdown = children && (
      children.includes('**') || 
      children.includes('*') || 
      children.includes('`') || 
      children.includes('#') || 
      children.includes('[') ||
      children.includes('>') ||
      children.includes('|') ||
      children.includes('---')
    );

    if (!hasMarkdown) {
      return (
        <Text fontSize={fontSize} color={color} noOfLines={noOfLines} {...props}>
          {children}
        </Text>
      );
    }

    return (
      <Box 
        fontSize={fontSize} 
        color={color} 
        noOfLines={noOfLines} 
        sx={{
          '& p': {
            margin: '0',
            lineHeight: 'inherit'
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '0',
            fontWeight: 'bold',
            lineHeight: 'inherit',
            fontSize: 'inherit'
          }
        }}
        {...props}
      >
        <MarkdownRenderer content={children} />
      </Box>
    );
  };

  return (
    <Box
      bg="white"
      borderRadius="20px"
      boxShadow="sm"
      border="1px solid #d1d5db"
      p={4}
      display="flex"
      flexDirection="column"
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: 'lg',
        borderColor: '#25575F',
      }}
    >
      {/* Conditional media section - only render if valid media exists */}
      {hasValidMedia() && (
        <Box position="relative" mb={3}>
          {isVideo && videoUrl ? (
            <>
              <video
                src={videoUrl}
                style={{
                  borderRadius: '16px',
                  width: '100%',
                  height: '240px',
                  objectFit: 'cover'
                }}
                muted
                autoPlay
                loop
              />
              <Box
                position="absolute"
                top={2}
                right={2}
                bg="rgba(0, 0, 0, 0.7)"
                color="white"
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                fontWeight="bold"
              >
                VIDEO
              </Box>
            </>
          ) : (
            <Image
              src={image}
              alt={title}
              borderRadius="16px"
              w="100%"
              h={{ base: '220px', md: '240px' }}
              objectFit="cover"
              fallback={<Box w="100%" h={{ base: '220px', md: '240px' }} bg="gray.200" borderRadius="16px" />}
            />
          )}
        </Box>
      )}
      <Flex align="center" mb={1} gap={2}>
        <Box as={MdLocalOffer} color="#25575F" />
        <Tag color="#25575F" bg="#F8F9F6" fontWeight="medium" fontSize="sm">{safeTag}</Tag>
      </Flex>
      <Text fontSize={{ base: 'md', md: 'lg' }} color="#222" fontWeight="semibold" mb={1} noOfLines={2}>
        {title}
      </Text>
      <MarkdownText color="gray.600" fontSize="sm" noOfLines={3}>
        {post_desc || desc || "No description available"}
      </MarkdownText>
    </Box>
  );
}

export default RelatedPostCard; 